import { useState } from 'react';
import { ShieldCheck, CheckCircle, XCircle, Loader2, Building2 } from 'lucide-react';
import { useVerifyToken, useSelectTenant } from '../../hooks/useAuth';
import type { TenantInfo } from '../../hooks/useAuth';
import { setTenantId } from '../../lib/auth';

interface VerifyViewProps {
  token: string;
  onSuccess: () => void;
}

export function VerifyView({ token, onSuccess }: VerifyViewProps) {
  const [verified, setVerified] = useState(false);
  const [tenants, setTenants] = useState<TenantInfo[] | null>(null);
  const verifyMutation = useVerifyToken();
  const selectTenantMutation = useSelectTenant();

  function handleVerify() {
    verifyMutation.mutate(token, {
      onSuccess: (data) => {
        if (data.selectedTenant) {
          // Single tenant — auto-selected, go straight to dashboard
          setVerified(true);
          setTimeout(onSuccess, 1000);
        } else if (data.tenants && data.tenants.length > 1) {
          // Multiple tenants — show selector
          setTenants(data.tenants);
        } else {
          // Shouldn't happen, but handle gracefully
          setVerified(true);
          setTimeout(onSuccess, 1000);
        }
      },
    });
  }

  function handleTenantSelect(tenantId: string) {
    selectTenantMutation.mutate(tenantId, {
      onSuccess: () => {
        setTenantId(tenantId);
        setVerified(true);
        setTimeout(onSuccess, 800);
      },
    });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-brand-950">
      <div className="w-full max-w-md px-4">
        <div className="card p-8 text-center">
          {/* Logo */}
          <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-600">
            <span className="text-2xl font-bold text-white">Z</span>
          </div>

          {verified ? (
            /* Success state */
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Verified!</h2>
              <p className="mt-2 text-sm text-slate-500">
                Redirecting to your dashboard...
              </p>
            </>
          ) : tenants ? (
            /* Multi-tenant selector */
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand-100">
                <Building2 className="h-6 w-6 text-brand-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Select Organization</h2>
              <p className="mt-2 mb-6 text-sm text-slate-500">
                You have access to multiple organizations. Choose one to continue.
              </p>

              <div className="space-y-2">
                {tenants.map((tenant) => (
                  <button
                    key={tenant.tenantId}
                    onClick={() => handleTenantSelect(tenant.tenantId)}
                    disabled={selectTenantMutation.isPending}
                    className="w-full flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 text-left hover:border-brand-300 hover:bg-brand-50 transition-colors disabled:opacity-50"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {tenant.displayName}
                      </p>
                      <p className="text-xs text-slate-500 capitalize">
                        {tenant.role}
                      </p>
                    </div>
                    {selectTenantMutation.isPending &&
                     selectTenantMutation.variables === tenant.tenantId ? (
                      <Loader2 className="h-4 w-4 animate-spin text-brand-500" />
                    ) : (
                      <span className="text-xs text-slate-400">&rarr;</span>
                    )}
                  </button>
                ))}
              </div>

              {selectTenantMutation.error && (
                <p className="mt-4 text-sm text-red-600">
                  Failed to select organization. Please try again.
                </p>
              )}
            </>
          ) : verifyMutation.error ? (
            /* Error state */
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Verification Failed</h2>
              <p className="mt-2 text-sm text-slate-500">
                This link may have expired or already been used. Magic links are single-use for security.
              </p>
              <button
                onClick={() => window.location.replace('/')}
                className="btn-primary mt-6"
              >
                Back to Login
              </button>
            </>
          ) : (
            /* Initial verify button — explicit click required (email scanner protection) */
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand-100">
                <ShieldCheck className="h-6 w-6 text-brand-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Verify Your Identity</h2>
              <p className="mt-2 text-sm text-slate-500">
                Click the button below to complete sign-in. This extra step protects against email security scanners.
              </p>

              <button
                onClick={handleVerify}
                disabled={verifyMutation.isPending}
                className="btn-primary mt-6 w-full"
              >
                {verifyMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  'Verify and Sign In'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
